import { useT } from '@kmgestao/i18n';
import { Badge, PageSectionCard, Skeleton } from '@kmgestao/ui';

import {
  formatBRL,
  formatDateBR,
  useOwnSubscription,
  type OwnSubscriptionWithPlan,
} from '../../../features/clinic-billing/index.js';

export interface CurrentPlanCardProps {
  clinicId: string;
}

/**
 * Card "Meu plano" — mostra plano contratado, valor, ciclo, status,
 * trial info (quando aplicável) e período de cobrança atual.
 *
 * Reusa `useOwnSubscription` (já existente em `clinic-billing`) sem
 * polling — esta tela não precisa atualização live (diferente da
 * `PaymentPendingPage` onde owner espera pagamento confirmar).
 */
export function CurrentPlanCard({ clinicId }: CurrentPlanCardProps) {
  const { t } = useT('clinics');
  const subQ = useOwnSubscription(clinicId);

  return (
    <PageSectionCard
      title={t('plan.current.title')}
      description={t('plan.current.description')}
      contentClassName="space-y-4"
    >
      {subQ.isLoading ? (
        <CurrentPlanSkeleton />
      ) : subQ.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {t('plan.current.loadError', {
            message: subQ.error instanceof Error ? subQ.error.message : '',
          })}
        </p>
      ) : subQ.data ? (
        <PlanDetails data={subQ.data} />
      ) : (
        <p className="text-sm text-muted-foreground">{t('plan.current.empty')}</p>
      )}
    </PageSectionCard>
  );
}

function PlanDetails({ data }: { data: OwnSubscriptionWithPlan }) {
  const { t } = useT('clinics');
  const { subscription, plan } = data;
  const cycleLabel =
    subscription.billing_cycle === 'yearly'
      ? t('plan.current.cycle.yearly')
      : t('plan.current.cycle.monthly');
  const price =
    subscription.billing_cycle === 'yearly' ? plan?.yearly_price_brl : plan?.monthly_price_brl;
  const trialEndsAt = formatDateBR(subscription.trial_ends_at);
  const periodStart = formatDateBR(subscription.current_period_start);
  const periodEnd = formatDateBR(subscription.current_period_end);

  return (
    <div className="space-y-6">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('plan.current.fields.name')} value={plan?.name_pt_br ?? '—'} />
        <Field label={t('plan.current.fields.cycle')} value={cycleLabel} />
        <Field label={t('plan.current.fields.price')} value={formatBRL(price ?? null)} />
        <Field
          label={t('plan.current.fields.status')}
          value={
            <Badge variant={statusBadgeVariant(subscription.status)}>
              {t(`plan.current.statusLabels.${subscription.status}`)}
            </Badge>
          }
        />
      </dl>

      {subscription.status === 'trialing' && trialEndsAt ? (
        <div className="rounded-lg border border-accent/40 bg-accent/5 p-4 text-sm">
          <p>
            <span aria-hidden="true">⏱️ </span>
            <strong className="text-foreground">{t('plan.current.trial.title')}</strong>{' '}
            {t('plan.current.trial.endingAt', { date: trialEndsAt })}
          </p>
        </div>
      ) : null}

      {periodStart && periodEnd ? (
        <p className="text-sm text-muted-foreground">
          {t('plan.current.period', { start: periodStart, end: periodEnd })}
        </p>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-base font-medium text-foreground">{value}</dd>
    </div>
  );
}

function statusBadgeVariant(
  status: string,
): 'default' | 'destructive' | 'success' | 'secondary' | 'outline' {
  switch (status) {
    case 'active':
      return 'success';
    case 'trialing':
      return 'default';
    case 'past_due':
    case 'unpaid':
      // Badge não tem variant 'warning' — usamos 'secondary' (cor neutra-muted)
      return 'secondary';
    case 'canceled':
    case 'paused':
    case 'incomplete':
      return 'destructive';
    default:
      return 'outline';
  }
}

function CurrentPlanSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-5 w-32" />
        </div>
      ))}
    </div>
  );
}
